/** 조직도 캔버스 격자 스냅(px) */
export const ORG_FLOW_GRID_PX = 20;

/** React Flow 부서 노드 카드 크기(화면 표시·fitView 계산용) */
export const ORG_DEPT_NODE_WIDTH = 188;
export const ORG_DEPT_NODE_MIN_HEIGHT = 56;
/** 노드 좌상단 기준 — 카드가 뷰포트 중앙에 오도록 하는 Y 오프셋(대략 카드 높이의 절반) */
export const ORG_DEPT_NODE_CENTER_OFFSET_Y = 32;
/** 헤더(부서명·경로) 영역 — 좌·우 핸들은 이 높이의 세로 중앙에 고정 (구성원 목록 높이와 무관) */
export const ORG_DEPT_HEADER_MIN_HEIGHT = 40;
/** 조직도 PNG 다운로드 시 제외할 UI (연결 핸들·삭제 버튼 등) */
export const SAP_ORG_EXPORT_HIDE_CLASS = 'sap-org-export-hide';

/** 휠 패닝 속도 (React Flow panOnScrollSpeed와 동일 수준) */
export const ORG_PAN_SCROLL_SPEED = 0.5;

/** 휠 deltaMode 보정 (xyflow panOnScroll과 동일) */
export const NormalizeOrgFlowWheelDelta = (event) => {
  if (event.deltaMode === 1) {
    return 20;
  }
  return 1;
};

/** Ctrl(Windows) / ⌘(macOS) + 휠 → 확대·축소 (React Flow에 위임) */
export const IsOrgFlowZoomWheel = (event) =>
  Boolean(event?.ctrlKey || event?.metaKey);

/**
 * 휠 패닝 방향 (Windows·macOS 동일 규칙)
 * - 휠: 위·아래만
 * - Shift + 휠: 좌·우만 (Win은 deltaY, Mac 트랙패드는 deltaX 우선)
 */
export const ResolveOrgFlowWheelPanDelta = (event) => {
  const scale = NormalizeOrgFlowWheelDelta(event);

  if (event.shiftKey) {
    const horizontal =
      Math.abs(event.deltaX) >= Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;
    return { deltaX: horizontal * scale, deltaY: 0 };
  }

  return { deltaX: 0, deltaY: event.deltaY * scale };
};

export const ShouldIgnoreOrgFlowWheel = (event) => {
  const target = event?.target;
  return target instanceof Element && Boolean(target.closest('.nowheel'));
};

/**
 * Ctrl/⌘+휠 한 칸(약 deltaY 100)당 log2 줌 변화량
 * 0.06 ≈ 4.2% 확대/축소 (xyflow 기본 0.002×10 배율보다 완만함)
 */
export const ORG_WHEEL_ZOOM_RATIO_PER_LINE = 0.06;

/** Ctrl/⌘+휠·트랙패드 핀치 확대 배율 */
export const ResolveOrgFlowWheelZoom = (
  event,
  currentZoom,
  minZoom = 0.2,
  maxZoom = 2
) => {
  const rawLines =
    event.deltaMode === 1 ? -event.deltaY : -event.deltaY / 100;
  const lines = Math.max(-2.5, Math.min(2.5, rawLines));
  const zoomFactor = 2 ** (lines * ORG_WHEEL_ZOOM_RATIO_PER_LINE);
  const next = currentZoom * zoomFactor;
  return Math.min(maxZoom, Math.max(minZoom, next));
};

/** fitView 시 노드가 화면을 과하게 채우지 않도록 상한 줌 */
export const ORG_FLOW_FIT_MAX_ZOOM = 0.82;
export const ORG_FLOW_FIT_PADDING = 0.22;

export const SnapOrgFlowPosition = (n) => {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n / ORG_FLOW_GRID_PX) * ORG_FLOW_GRID_PX;
};

/** dept_ / dept- 접두어 제거 → Flow 노드 id(부서코드) */
export const StripDeptPrefix = (id) => {
  if (id == null || id === '') return '';
  return String(id).replace(/^dept[-_]/, '');
};

export const GetDeptNodeId = (dept) => {
  const raw = dept?.id ?? dept?.depCd ?? dept?.DEP_CD ?? '';
  return StripDeptPrefix(raw) || String(raw);
};

/**
 * lines.path → React Flow edge type
 * 조직도 '직선'은 핸들 간 대각선(straight)이 아니라 수평·수직 꺾은선(smoothstep)
 */
export const LinePathToEdgeType = (path) => {
  switch (path) {
    case 'smooth':
      return 'default';
    case 'grid':
      return 'step';
    case 'straight':
    default:
      return 'smoothstep';
  }
};

export const GetEdgePathOptions = (path) => {
  switch (path) {
    case 'grid':
      return { borderRadius: 0 };
    case 'straight':
    default:
      return { borderRadius: 8 };
    case 'smooth':
      return {};
  }
};

/** 노드 상대 위치에 맞는 출발·도착 핸들 (대각선 연결 방지) */
export const ResolveConnectionHandles = (
  sourceNode,
  targetNode,
  sourceHandle,
  targetHandle
) => {
  const normalize = (h) =>
    !h || h === 'auto' ? null : String(h).toLowerCase();
  let sh = normalize(sourceHandle);
  let th = normalize(targetHandle);
  if (!sourceNode || !targetNode) {
    return { sourceHandle: sh, targetHandle: th };
  }
  const sx = sourceNode.position?.x ?? 0;
  const sy = sourceNode.position?.y ?? 0;
  const tx = targetNode.position?.x ?? 0;
  const ty = targetNode.position?.y ?? 0;
  const dx = tx - sx;
  const dy = ty - sy;

  if (!sh) {
    if (Math.abs(dx) >= Math.abs(dy)) {
      sh = dx >= 0 ? 'right' : 'left';
    } else {
      sh = dy >= 0 ? 'bottom' : 'top';
    }
  }
  if (!th) {
    if (Math.abs(dx) >= Math.abs(dy)) {
      th = dx >= 0 ? 'left' : 'right';
    } else {
      th = dy >= 0 ? 'top' : 'bottom';
    }
  }
  return { sourceHandle: sh, targetHandle: th };
};

export const EdgeTypeToLinePath = (edgeType) => {
  switch (edgeType) {
    case 'default':
    case 'bezier':
      return 'smooth';
    case 'step':
    case 'smoothstep':
      return 'grid';
    case 'straight':
    default:
      return 'straight';
  }
};

export const MapDepartmentsToFlowNodes = (departments = [], members = []) => {
  return (departments || []).map((dept) => {
    const nodeId = GetDeptNodeId(dept);
    const deptMembers = (members || []).filter((m) => {
      const mid = StripDeptPrefix(m.departmentId ?? m.DEPARTMENT_ID);
      return mid === nodeId || String(m.departmentId) === String(dept.id);
    });
    return {
      id: nodeId,
      type: 'department',
      position: {
        x: SnapOrgFlowPosition(dept.x ?? 0),
        y: SnapOrgFlowPosition(dept.y ?? 0),
      },
      data: { dept, members: deptMembers },
      draggable: true,
      connectable: true,
    };
  });
};

/** mappedNodes 동기화 시 드래그 위치·DOM 측정 크기 유지 */
export const MergeFlowNodesFromMapped = (mappedNodes, prevNodes = []) => {
  const prevById = new Map((prevNodes || []).map((n) => [n.id, n]));
  return (mappedNodes || []).map((mn) => {
    const prev = prevById.get(mn.id);
    if (!prev) {
      return mn;
    }
    return {
      ...mn,
      position: prev.position ?? mn.position,
      width: prev.width ?? prev.measured?.width ?? mn.width,
      height: prev.height ?? prev.measured?.height ?? mn.height,
      measured: prev.measured ?? mn.measured,
      dragging: prev.dragging,
      selected: prev.selected,
    };
  });
};

const NormalizeFlowHandle = (anchor) => {
  if (!anchor || anchor === 'auto') return null;
  return anchor;
};

export const MapLinesToFlowEdges = (
  lines = [],
  defaultPath = 'straight',
  nodes = []
) => {
  const nodeById = new Map((nodes || []).map((n) => [n.id, n]));
  return (lines || [])
    .map((line) => {
      const source = StripDeptPrefix(
        line.sourceId ?? line.source ?? line.SourceId ?? line.Source
      );
      const target = StripDeptPrefix(
        line.targetId ?? line.target ?? line.TargetId ?? line.Target
      );
      if (!source || !target) return null;
      const path = line.path ?? line.Path ?? defaultPath;
      const edgeType = LinePathToEdgeType(path);
      const sourceNode = nodeById.get(source);
      const targetNode = nodeById.get(target);
      const { sourceHandle, targetHandle } = ResolveConnectionHandles(
        sourceNode,
        targetNode,
        NormalizeFlowHandle(line.startAnchor ?? line.StartAnchor ?? null),
        NormalizeFlowHandle(line.endAnchor ?? line.EndAnchor ?? null)
      );
      return {
        id: String(line.id ?? `edge_${source}_${target}_${Math.random()}`),
        source,
        target,
        sourceHandle,
        targetHandle,
        type: edgeType,
        pathOptions: GetEdgePathOptions(path),
        animated: false,
        style: {
          stroke: line.strokeColor ?? line.StrokeColor ?? '#333',
          strokeWidth: line.strokeWidth ?? line.StrokeWidth ?? 2,
        },
        data: {
          path,
          curveness: line.curveness ?? line.Curveness ?? 0.5,
        },
      };
    })
    .filter(Boolean);
};
